// Full Calendar Plugins
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";

// Notification
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

// eslint-disable-next-line object-curly-newline
import { ref, computed, watch, onMounted } from "@vue/composition-api";
import store from "@/store";
import { getDateWithoutTime } from "@/libs/helper";

export default function useCalendar() {
  const toast = useToast();

  const refCalendar = ref(null);

  let calendarApi = null;

  /*
    onMounted(() => {
        console.log('useCalendar onMounted')
        calendarApi = refCalendar.value.getApi()
        if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|ipad|iris|kindle|Android|Silk|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(navigator.userAgent) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(navigator.userAgent.substr(0, 4))) {
            calendarApi.changeView('listMonth');
        }
    })
    */

  const resetView = () => {
    calendarApi.changeView("dayGridMonth");
  };

  const refetchEvents = () => {
    calendarApi.refetchEvents();
  };

  const fetchEvents = (info, successCallback) => {
    if (!info) return;
    // store.dispatch('meeting/fetchEvents', {calendars: selectedCalendars.value, is_admin:false}).then(response => {
    store
      .dispatch("task/fetchTasks", {
        date_from: getDateWithoutTime(info.startStr),
        date_to: getDateWithoutTime(info.endStr),
        include_company_data: 1,
      })
      .then((response) => {
        let tasks = [];

        // If neet to set the day correctly, first need to change the full day to false, then set the end to "T23:59:00"

        response.data.tasks.forEach((item) => {
          let date = item.deadline_date
            ? item.deadline_date.slice(0, 10)
            : item.created_at.slice(0, 10);
          let start = date + "T00:00:00";
          let end = date + "T23:59:00";
          let allDay = true;
          tasks.push({ ...item, start, end, allDay, subtitle: item.title });
        });

        //tasks.push({start:'2022-04-16', end: '2022-04-16', color:'red',display: 'background'})

        successCallback(tasks);
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error fetching calendar events",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const calendarOptions = ref({
    plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin],
    initialView: "dayGridMonth",
    headerToolbar: {
      start: "sidebarToggle, prev,next, title",
      end: "dayGridMonth,listMonth",
    },
    //timeZone: 'Asia/Hong_Kong',
    events: fetchEvents,
    editable: false,
    eventResizableFromStart: true,
    dragScroll: true,
    dayMaxEvents: 50,
    eventContent: function (arg) {
      var event = arg.event;
      let record = event._def.extendedProps;
      console.log(record);

      var customHtml = "";

      if (arg.event.display != "background") {
        let color = record.stage.background_color;
        customHtml +=
          "<div style='position:relative; padding:5px; border-radius:5px; width:100%; overflow: hidden; background:" +
          color +
          "; color:white; box-shadow:0 0 10px 1px rgb(165 133 61 / 70%);'>";
        if (record.client) {
          customHtml +=
            "<span style='margin-top:5px;'>" +
            record.client.client_code +
            " " +
            record.client.company_name_cn +
            "</span><br>";
        }
        customHtml +=
          "<span style='margin-top:5px;'>" + record.subtitle + "</span><br>";
        customHtml +=
          "<span style='margin-top:5px;'>" +
          record.creator.name +
          "</span><br>";
        customHtml +=
          "<span style='margin-top:5px;'>" + record.stage.stage + "</span>";
        customHtml += "</div>";
      } else {
        customHtml +=
          '<br><span style="color:white;">' + record.holiday_name + "</span>";
      }

      return { html: customHtml };
    },
    eventResize({ event: resizedEvent }) {
      updateEvent(grabEventDataFromEventApi(resizedEvent));
    },
    direction: computed(() => (store.state.appConfig.isRTL ? "rtl" : "ltr")),
    rerenderDelay: 350,
  });

  return {
    refCalendar,
    calendarOptions,
    refetchEvents,
    fetchEvents,
    resetView,
  };
}
